import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ChevronDown,
  Vote,
  Trophy,
  ShieldCheck,
  Users,
} from "lucide-react";
import Sidebar, { MobileHeader } from "./Sidebar";
import { openAppStore } from "../lib/appStores";

const ACCENT = "#F5A623";

const ROLES = [
  {
    role: "ZEUS: LEADER AND RAPPER",
    description:
      "Mission #1: win 3 live freestyle rap pagents, ranked or for fun, to inherit the crown of Zeus.",
    image: "/images/boygroup-zeus-icon.png",
    color: "#F5E04A",
  },
  {
    role: "POSEIDON: DANCER",
    description:
      "Mission #1: win 3 live dance pagents, ranked or for fun, to claim the crown of Poseidon.",
    image: "/images/boygroup-poseidon-icon.png",
    color: "#247CCA",
  },
  {
    role: "HADES: VOCALIST",
    description:
      "Mission #1: win 3 live karaoke pagents, ranked or for fun, to claim the crown of Hades.",
    image: "/images/boygroup-hades-icon.png",
    color: "#F22600",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Choose Your God",
    icon: Users,
    body: "Compete as Zeus (Leader and Rapper), Poseidon (Dancer), or Hades (Vocalist). Mission #1 is 3 live wins in that Role's category.",
  },
  {
    number: "02",
    title: "Complete Missions",
    icon: Sparkles,
    body: "Each Role opens with Mission #1: 3 live wins in freestyle rap, dance, or karaoke. Ranked and for fun both count. Further Missions unlock as the Contest rises.",
  },
  {
    number: "03",
    title: "Earn Votes",
    icon: Vote,
    body: "The Pagent community decides. Advancement is determined by community voting. No judges, no lottery.",
  },
  {
    number: "04",
    title: "Inherit the Crown",
    icon: ShieldCheck,
    body: "Finalists who endure the climb may inherit the crown of their God and take a seat in OLYMPUS is RISING.",
  },
  {
    number: "05",
    title: "Form the Boy Group",
    icon: Trophy,
    body: "The winners of Zeus, Poseidon, and Hades shall form the first 100% online-voted boy group, OLYMPUS is RISING.",
  },
];

const PRIZES = [
  {
    title: "A Spot in OLYMPUS is RISING",
    body: "The title and place among the official Pagent Boy Group.",
  },
  {
    title: "Official Recognition",
    body: "Official recognition on the Pagent platform.",
  },
  {
    title: "Winning Role Title",
    body: "The title associated with their winning God: Zeus, Poseidon, or Hades.",
  },
];

const ELIGIBILITY = [
  "be at least 13 years old;",
  "have a Pagent account in good standing;",
  "comply with Pagent's Terms of Service; and",
  "reside in a jurisdiction where participation is permitted by law.",
];

const FAQS = [
  {
    question: "Is the Contest free?",
    answer:
      "Yes. There is no purchase or payment required to enter or participate.",
  },
  {
    question: "How are Winners selected?",
    answer:
      "Winners are selected entirely through votes cast by the Pagent community.",
  },
  {
    question: "What is OLYMPUS is RISING?",
    answer:
      "OLYMPUS is RISING is the name of the first ever 100% online-voted boy group assembled through this Contest.",
  },
  {
    question: "Can I compete in more than one Role?",
    answer:
      "Yes. You may pursue more than one God, but the Final Mission shall demand you choose one crown.",
  },
  {
    question: "When does the Boy Group Contest open?",
    answer:
      "The Boy Group Contest opens November 1st, 2026 in the Pagent app.",
  },
];

export default function BoyGroup() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Helmet>
        <title>OLYMPUS is RISING: Pagent Boy Group Contest</title>
        <meta
          name="description"
          content="Lo! The first boy group ever chosen wholly by the voice of the internet doth rise. Decide ye which idols deserve to inherit the crown from Gods Zeus, Poseidon, and Hades, for OLYMPUS is RISING."
        />
        <meta
          property="og:title"
          content="OLYMPUS is RISING: Pagent Boy Group Contest"
        />
        <meta
          property="og:description"
          content="The first ever 100% online-voted boy group. Inherit the crown from Zeus, Poseidon, and Hades."
        />
        <meta
          property="og:image"
          content="https://www.pagent.tv/images/boygroup-thumbnail.jpg"
        />
        <meta property="og:url" content="https://www.pagent.tv/boygroup" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Pagent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="OLYMPUS is RISING: Pagent Boy Group Contest"
        />
        <meta
          name="twitter:description"
          content="Decide which idols deserve to inherit the crown from Gods Zeus, Poseidon, and Hades."
        />
        <meta
          name="twitter:image"
          content="https://www.pagent.tv/images/boygroup-thumbnail.jpg"
        />
        <meta name="theme-color" content="#000000" />
        <link rel="canonical" href="https://www.pagent.tv/boygroup" />
      </Helmet>

      <Sidebar />

      <div className="min-h-screen md:pl-60">
        <MobileHeader />

        {/* Summit hero — peak at top, fade into page */}
        <section className="relative w-full overflow-hidden">
          <div className="relative h-[420px] w-full sm:h-[520px] md:h-[600px]">
            <img
              src="/images/boygroup-olympus-header.png"
              alt="Mount Olympus summit"
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-b from-transparent via-black/70 to-black" />
            <div className="absolute inset-x-0 bottom-10 z-10 flex flex-col items-center px-6 text-center sm:bottom-14">
              <span
                className="mb-4 inline-block rounded-full px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] text-black"
                style={{ backgroundColor: ACCENT }}
              >
                Pagent Contest
              </span>
              <h1 className="text-3xl font-black uppercase leading-tight tracking-tight text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)] sm:text-5xl md:text-6xl">
                OLYMPUS is RISING
              </h1>
              <p className="mt-3 text-base font-semibold text-white/80 sm:text-xl">
                The first 100% online-voted boy group.
              </p>
            </div>
          </div>
        </section>

        {/* Archaic blurb */}
        <section className="px-4 pt-8">
          <div className="mx-auto max-w-3xl space-y-4 text-center">
            <p className="text-lg leading-relaxed text-white/75 sm:text-xl">
              Lo! The first boy group ever chosen wholly by the voice of the
              internet doth rise. Decide ye which idols deserve to inherit the
              crown from Gods Zeus, Poseidon, and Hades, for{" "}
              <span className="font-bold text-white">OLYMPUS is RISING</span>.
            </p>
            <ChevronDown className="mx-auto h-9 w-9 animate-bounce text-white/70" />
          </div>
        </section>

        {/* How it Works */}
        <section className="px-4 pt-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-white sm:text-3xl">
              How it Works
            </h2>
            <div className="space-y-4">
              {STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.number}
                    className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${ACCENT}22` }}
                      >
                        <Icon className="h-6 w-6" style={{ color: ACCENT }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span
                            className="text-xs font-bold uppercase tracking-[0.2em]"
                            style={{ color: ACCENT }}
                          >
                            Step {step.number}
                          </span>
                          <h3 className="text-lg font-bold text-white sm:text-xl">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-sm leading-relaxed text-white/70 sm:text-base">
                          {step.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Roles */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-5xl">
            <h2 className="mb-3 text-center text-2xl font-bold text-white sm:text-3xl">
              Choose Your God
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-center text-white/60">
              Three crowns. Three Roles. Compete as Zeus, Poseidon, or Hades.
              Mission #1 awaits in the Pagent app.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {ROLES.map((item) => (
                <div
                  key={item.role}
                  className="overflow-hidden rounded-2xl p-px"
                  style={{
                    background: `linear-gradient(135deg, ${item.color}, ${item.color}33)`,
                  }}
                >
                  <div
                    className="flex h-full flex-col items-center gap-4 rounded-[15px] p-5 text-center"
                    style={{ backgroundColor: "rgba(9, 9, 11, 0.96)" }}
                  >
                    <img
                      src={item.image}
                      alt=""
                      className="h-28 w-28 rounded-full object-cover"
                    />
                    <h3 className="text-base font-bold text-white md:text-lg">
                      {item.role}
                    </h3>
                    <p className="text-sm text-white/60">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prizes */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-3 text-center text-2xl font-bold text-white sm:text-3xl">
              Prizes
            </h2>
            <p className="mb-8 text-center text-white/60">
              Those who inherit the crown receive:
            </p>
            <div className="space-y-4">
              {PRIZES.map((prize, index) => (
                <div
                  key={prize.title}
                  className="rounded-2xl border bg-white/5 p-6"
                  style={{
                    borderColor:
                      index === 0
                        ? "rgba(245, 166, 35, 0.4)"
                        : "rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h3
                    className="mb-2 text-xl font-bold"
                    style={{ color: index === 0 ? ACCENT : undefined }}
                  >
                    {prize.title}
                  </h3>
                  <p className="text-white/70">{prize.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-6 text-center text-2xl font-bold text-white sm:text-3xl">
              Eligibility
            </h2>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
              <p className="mb-4 font-semibold text-white">
                To participate, you must:
              </p>
              <ul className="mb-6 list-disc space-y-2 pl-5 text-white/70">
                {ELIGIBILITY.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-white sm:text-3xl">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={faq.question}
                    className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-white/5"
                    >
                      <h3 className="pr-4 text-base font-semibold text-white sm:text-lg">
                        {faq.question}
                      </h3>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        style={{ color: ACCENT }}
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-white/10 px-6 py-4">
                        <p className="leading-relaxed text-white/70">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 py-20">
          <div className="mx-auto max-w-3xl space-y-7 text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Ready to Ascend?
            </h2>
            <p className="text-lg text-white/70">
              Download the Pagent app. When the summit opens on November 1st,
              2026, choose your God and begin Mission #1.
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-lg font-bold text-black shadow-2xl transition-opacity hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
              onClick={openAppStore}
            >
              Enter on the Pagent App
            </button>
          </div>
        </section>

        <footer className="border-t border-white/10 px-4 py-8 text-center text-sm text-white/40">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/terms" className="hover:text-white/70">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-white/70">
              Privacy
            </Link>
            <Link to="/faq" className="hover:text-white/70">
              FAQ
            </Link>
            <Link to="/updates" className="hover:text-white/70">
              Patch updates
            </Link>
            <Link to="/support" className="hover:text-white/70">
              Support
            </Link>
          </div>
          <p className="mt-3">© 2026 Pagent. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
